import { pipe } from 'fp-ts/function';
import { curry, filter, partition, some } from 'lodash';
import moment from 'moment';

import { TermToAssign } from '@domain/districts/types';

import {
  buildUnassignedRoleTerms,
  groupDOsByRole,
  sortRolesByRequired,
} from '@use-cases/districts/helpers';

import { getFormattedRYRange, isFutureRY } from '@utils/datetime';

import {
  DISTRICT_OFFICER_GOVERNOR_ROLES,
  DISTRICT_OFFICER_ROLES_TO_OMIT,
  DORole,
  DORoles,
  OfficerAssignedToRole,
  Role,
  RoleParam,
  ROLES_REQUIRED_BY_POLICY,
  UnassignedDistrictOfficerRoleTerm as Unassigned,
} from '@domui-domain/districts';
import { DynamicData } from '@domui-domain/type';
import { getOfficerIdsByTermStartDate } from '@domui-use-cases/districts';
import { Membership, MembershipType } from '@domui-utils/types';

import { DistrictOfficer as DO } from '@typings/graphql';

type OfficerType = {
  id: string;
  firstName: string;
  lastName: string;
  middleName: string | null;
  localizedName: string | null;
  prefix: string | null;
  suffix: string | null;
  photoUri: string | null;
  riIndividualId: string;
  primaryEmail: { id: string; type: string; address: string };
  primaryPhone: {
    id: string;
    country: string;
    number: string;
    areaCode: string | null;
    countryId: string;
    extension: string | null;
    isFax: boolean;
  };
  preferredLanguage: string | null;
  districtLeadership: DO[];
  membership: Array<{
    id: string;
    clubId: string;
    clubName: string;
    riClubId: string;
    clubType: string;
    type: string | null;
    districtId: string;
    riDistrictId: string;
    admissionDate: string;
    terminationDate: string | null;
  }>;
};

export const extractOfficersData = (data: DynamicData) => {
  const officers = data?.districtOfficersDetails || [];
  const today = new Date();

  const getCurrentMembership = (memberships: Membership[]) => {
    if (!memberships?.length) return null;

    if (memberships.length === 1) return memberships[0];

    const active = memberships.filter(m => {
      const admission = m.admissionDate ? new Date(m.admissionDate) : null;
      const termination = m.terminationDate
        ? new Date(m.terminationDate)
        : null;

      const afterAdmission = admission ? today >= admission : true;
      const beforeTermination = termination ? today <= termination : true;

      return afterAdmission && beforeTermination;
    });

    if (!active.length) return null;

    const priority: Record<MembershipType, number> = {
      [MembershipType.Member]: 1,
      [MembershipType.Rotaractor]: 2,
      [MembershipType.Honorary]: 3,
      [MembershipType.RotaractHonorary]: 4,
    };

    active.sort((a, b) => {
      const pa = priority[a.type as MembershipType] ?? 999;
      const pb = priority[b.type as MembershipType] ?? 999;
      return pa - pb;
    });

    return active[0];
  };

  return officers.flatMap((officer: OfficerType) => {
    const selectedMembership = getCurrentMembership(
      officer.membership as Membership[]
    );

    return (
      officer?.districtLeadership?.map((leadership: DO) => ({
        ...leadership,
        individual: {
          id: officer.id,
          firstName: officer.firstName,
          lastName: officer.lastName,
          middleName: officer.middleName,
          prefix: officer.prefix,
          suffix: officer.suffix,
          localizedName: officer.localizedName,
          primaryEmail: officer.primaryEmail?.address || null,
          primaryPhone: officer.primaryPhone?.number || null,
          photoUri: officer.photoUri,
          clubName: selectedMembership?.clubName || null,
          clubType: selectedMembership?.clubType || null,
          clubId: selectedMembership?.clubId || null,
          membership: selectedMembership,

          __typename: 'OfficerIndividual',
        },
        __typename: 'DistrictOfficer',
      })) ?? []
    );
  });
};

export const filterActiveDistrictOfficer = (
  extractedOfficersData: DynamicData | null,
  termYear: string,
  riDistrictId: number | null | undefined,
  allRoles: Role[] = []
) => {
  const yearStart = moment(`${Number(termYear) - 1}-07-01`);
  const yearEnd = moment(`${termYear}-06-30`);

  return extractedOfficersData?.filter((leadership: DynamicData) => {
    const roleStart = moment(leadership?.startDate);
    const roleEnd = moment(leadership?.endDate);

    const matchingRole = allRoles.find(role => role.id === leadership?.roleId);

    const termLength = matchingRole?.detail?.TermLength || 1;
    const isThreeYearRole = termLength === 3;

    const include = isThreeYearRole
      ? roleStart.isSameOrBefore(yearEnd) && roleEnd.isSameOrAfter(yearStart)
      : roleStart.isBetween(yearStart, yearEnd, null, '[]');

    return include && leadership?.riDistrictId === riDistrictId?.toString();
  });
};

export const getAssignedFuture3yrTermRoleIds = (
  future3yrTermOfficersData: DynamicData,
  term: TermToAssign | null
) =>
  getOfficerIdsByTermStartDate(
    extractOfficersData(future3yrTermOfficersData),
    getFormattedRYRange(Number(term?.endDate) + 1).startYear
  );

const getAssignableTerm = (term: DO | Unassigned): boolean | undefined =>
  (term as Unassigned).isAssignable;

const getRoleWithAssignableTerm = (role: DORole): boolean =>
  some(role.terms, getAssignableTerm);

const filterUnassignedRoles = (result: DORoles): DORole[] =>
  filter(result, getRoleWithAssignableTerm);

const filterRoles = (roles: Role[]): Role[] =>
  roles.filter(
    ({ value, detail: { IsOfficer } }) =>
      IsOfficer && !DISTRICT_OFFICER_ROLES_TO_OMIT.includes(value)
  );

const filterDOsByRole = (officers: DO[], RY: number = 0): DO[] =>
  officers?.filter(
    ({ isDelegated, role, endDate }) =>
      !(isDelegated || DISTRICT_OFFICER_ROLES_TO_OMIT.includes(role)) &&
      (isFutureRY(RY)
        ? moment(endDate).isSameOrAfter(
            moment(getFormattedRYRange(RY).startYear),
            'D'
          )
        : true)
  );
const sortRoles = curry(sortRolesByRequired)(ROLES_REQUIRED_BY_POLICY);

const splitRoles = (roles: DORoles): DORoles[] =>
  partition(roles, ({ role }) =>
    DISTRICT_OFFICER_GOVERNOR_ROLES.includes(role)
  );

const filterUnfilledDGRoles = (roles: DORoles): DORoles =>
  roles.filter(({ terms }) => !!terms.length);

const buildUnassignedRoleTermsForCAs = (
  year: number,
  roles: DORoles
): DORoles => roles.map(curry(buildUnassignedRoleTerms)(year));

const processRolesForManager = (year: number, roles: DORoles): DORoles => {
  const [governors, nonGovernors] = splitRoles(roles);

  return [
    ...filterUnfilledDGRoles(governors),
    ...buildUnassignedRoleTermsForCAs(year, nonGovernors),
  ];
};

const filterDORolesForViewer = (officers: DORoles) =>
  officers.filter(officer => officer.terms.length > 0);

const getDORolesForManager = ({ roles, officers, year }: RoleParam): DORoles =>
  pipe(
    groupDOsByRole(
      filterRoles(roles),
      filterDOsByRole(officers) as OfficerAssignedToRole[],
      Number(year)
    ),
    curry(processRolesForManager)(Number(year))
  );

const getDORolesForViewer = ({ roles, officers, year }: RoleParam): DORoles =>
  pipe(
    groupDOsByRole(
      filterRoles(roles),
      filterDOsByRole(officers, Number(year)) as OfficerAssignedToRole[],
      Number(year),
      true
    ),
    filterDORolesForViewer
  );

export const getDORoles = (isManager: boolean, params: RoleParam): DORoles[] =>
  pipe(
    (isManager ? getDORolesForManager : getDORolesForViewer)(params),
    sortRoles,
    splitRoles
  );

export const getDOUnassignedRoles = (params: RoleParam) =>
  pipe(
    getDORolesForManager(params),
    filterUnassignedRoles,
    sortRoles,
    splitRoles
  );
